import { useState, useEffect } from 'react';
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import { useSelector } from 'react-redux';
 
const CustomModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  onSubmit,
  showButton,
  showSubmitButton = true,
  showCancelButton = true,
  fontSize = "16px",
  fontWeight = "400",
  subtitleFontSize = "14px",
  subtitleFontWeight = "400",
  children,
  modalSize = "",
  minHeight = "200px",
  maxHeight = "550px",
  isCentered = false,
  submitButtonText = "Save", // New prop with default value "Save"
  submitButtonDisabled = false, // New prop for disabling submit button
  zIndex = 9999, // New prop for z-index control
  headerActions = null,

}) => {
  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  
  if (!isOpen) return null;

  return (
    <div
      className="modal fade show d-block"
      style={{ 
        backgroundColor: "rgba(0, 0, 0, 0.75)", 
        zIndex: isOpen ? zIndex : -1,
        backdropFilter: "blur(3px)",
        WebkitBackdropFilter: "blur(3px)",
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "auto"
      }}
      onClick={onClose}
    >
      <div
        className={"modal-dialog " + modalSize}
        style={{
          display: isCentered ? "flex" : "block",
          justifyContent: isCentered ? "center" : "initial",
          alignItems: isCentered ? "center" : "initial",
          minHeight: isCentered ? "100vh" : "auto",
          margin: isCentered ? "0 auto" : "0.5rem auto",
          position: "relative",
          zIndex: zIndex + 1
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal-content d-flex flex-column"
          style={{
            borderRadius: "20px",
            minHeight: minHeight,
            maxHeight: maxHeight,
            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
            border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
            color: isDarkMode ? '#ffffff' : '#000000',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
            position: 'relative',
            zIndex: zIndex + 2,
            overflow: "hidden",
          }}
        >
          <div
            className="modal-header flex-shrink-0 d-flex justify-content-between align-items-start"
            style={{ 
              border: "none", 
              paddingBottom: "0px",
              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
              color: isDarkMode ? '#ffffff' : '#000000',
              gap: "12px",
            }}
          >
            <div className="flex-grow-1 min-w-0">
              <h5
                className="modal-title"
                style={{ 
                  fontSize: fontSize, 
                  fontWeight: fontWeight,
                  color: isDarkMode ? '#ffffff' : '#000000',
                }}
              >
                {title}
              </h5>
              {subtitle && (
                <p
                  className="modal-subtitle"
                  style={{
                    fontSize: subtitleFontSize,
                    fontWeight: subtitleFontWeight,
                    margin: "8px 0 0 0",
                    color: isDarkMode ? '#adb5bd' : "#666",
                  }}
                >
                  {subtitle}
                </p>
              )}
            </div>
            <div className="d-flex align-items-start gap-2 flex-shrink-0">
              {headerActions}
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                style={{ marginTop: headerActions ? "4px" : undefined }}
              ></button>
            </div>
          </div>
          <div
            className="modal-body flex-grow-1"
            style={{
              overflowY: "auto",
              overflowX: "hidden",
              minHeight: 0,
              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
              color: isDarkMode ? '#ffffff' : '#000000',
            }}
          >
            {children}
          </div>

          {showButton && (
            <div
              className="modal-footer flex-shrink-0"
              style={{ border: "none", paddingTop: "0.75rem", paddingBottom: "1rem" }}
            >
              {showCancelButton && (
                <button
                  onClick={onClose}
                  type="button"
                  className="btn"
                  style={{ ...buttonStyles.base, ...buttonStyles.cancel }}
                >
                  Cancel
                </button>
              )}

              {showSubmitButton && (
                <button
                  onClick={onSubmit}
                  type="button"
                  className="btn"
                  disabled={submitButtonDisabled}
                  style={{ 
                    ...buttonStyles.base,
                    backgroundColor: submitButtonDisabled ? "#C4C4C4" : themeColor,
                    background: submitButtonDisabled ? "#C4C4C4" : `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    border: `1px solid ${themeColor}`,
                    boxShadow: submitButtonDisabled ? "none" : `0 4px 15px ${themeColor}40`,
                    transition: 'all 0.3s ease',
                    ...(submitButtonDisabled ? buttonStyles.disabled : {})
                  }}
                  onMouseEnter={(e) => {
                    if (!submitButtonDisabled) {
                      e.target.style.boxShadow = `0 6px 20px ${themeColor}60`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!submitButtonDisabled) {
                      e.target.style.boxShadow = `0 4px 15px ${themeColor}40`;
                    }
                  }}
                >
                  {submitButtonText} {/* Use the new prop here */}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const buttonStyles = {
  base: {
    fontFamily: "Open Sans, sans-serif",
    fontWeight: "600",
    minWidth: "107px",
    borderRadius: "10px",
    height: "40px",
    padding: "8px 16px",
    border: "none",
    outline: "none",
    fontSize: "14px",
    lineHeight: "1.25",
    cursor: "pointer",
    color: "white",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  cancel: {
    backgroundColor: "#C4C4C4",
    color: "#000",
  },
  disabled: {
    backgroundColor: "#C4C4C4",
    color: "#666",
    cursor: "not-allowed",
    opacity: 0.6,
  },
};

export default CustomModal;